/**
 * Analytics — everything the browser can say about a visit, and nothing
 * about how it is written down. Pure state, zero I/O: `track$` carries one
 * timeline event as the page that caused it knows it, `heartbeat$` is the
 * beat a visible tab produces, `analyticsOptOut` mirrors the buyer
 * document's standing objection, and `PAGE_BY_ROUTE` names which storefront
 * surface a route identity is. `web/src/analytics/buyer.sync.ts` is
 * the sole subscriber and the sole writer, so a surface mounted with no
 * reconciler beside it simply measures nothing.
 *
 * Nothing here touches device storage, in either direction: the opt-out is a
 * memory cell fed by the buyer's own document, and the first-touch readings
 * below are taken from the URL and the referrer header of the arrival that
 * is happening, never from anything a previous one left behind.
 */
import type { ActiveRoute } from "@mhmo91/schmancy";
import { state } from "@mhmo91/schmancy/state";
import { Buyer } from "@momo/types";
import {
	defer,
	distinctUntilChanged,
	EMPTY,
	fromEvent,
	map,
	type Observable,
	Subject,
	scan,
	startWith,
	switchMap,
	takeWhile,
	timer,
	timestamp,
} from "rxjs";

/**
 * The storefront surface behind each route identity the root area reports.
 * An area names a route by the declared `when` key it matched, and by the
 * mounted element's own tag when it has no `when` to give — its own default
 * at boot. Both spellings are listed wherever a route can be reported under
 * either. An identity absent from this map is a surface the console owns:
 * sign-in and every admin route resolve to nothing and record nothing.
 *
 * The list of what this browser bought and the ticket it retrieves are both
 * `order`: the vocabulary is closed at five and names the surface a visitor
 * is on, not the route that carried them there.
 */
const PAGE_BY_ROUTE: Readonly<Record<string, Buyer.Page>> = {
	storefront: "landing",
	"storefront-view": "landing",
	"/o/:orgId": "landing",
	"/:eventId": "event",
	"/o/:orgId/:eventId": "event",
	order: "order",
	"order-view": "order",
	orders: "order",
	"orders-view": "order",
	refund: "refund",
	"refund-view": "refund",
	legal: "legal",
	"legal-page": "legal",
};

/**
 * The storefront surface a route is, or null when it is an operator one.
 * Every write this emitter makes is gated on a non-null answer here — the
 * record itself included, so a member working in the console never becomes a
 * buyer document and never beats: the visitor whose visit is being measured
 * is by definition someone standing on the storefront.
 */
export function storefrontSurface(
	route: ActiveRoute,
): { readonly page: Buyer.Page; readonly eventId: string | null } | null {
	const page = PAGE_BY_ROUTE[route.when ?? route.component] ?? null;
	if (page === null) return null;
	const eventId = route.params?.eventId;
	return { page, eventId: typeof eventId === "string" ? eventId : null };
}

/** The report's compatibility dimensions. Only this coarse result leaves the
 * browser; the source strings stay in memory for the duration of the call. */
export function clientProfile(
	userAgent: string,
	width: number,
	height: number,
): Buyer.ClientProfile {
	const ua = userAgent.toLowerCase();
	const os = ua.includes("android")
		? "android"
		: ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod")
			? "ios"
			: ua.includes("mac os")
				? "macos"
				: ua.includes("windows")
					? "windows"
					: ua.includes("cros")
						? "chromeos"
						: ua.includes("linux")
							? "linux"
							: "other";
	const browser = ua.includes("edg/")
		? "edge"
		: ua.includes("samsungbrowser")
			? "samsung"
			: ua.includes("firefox/")
				? "firefox"
				: ua.includes("chrome/") || ua.includes("crios/")
					? "chrome"
					: ua.includes("safari/")
						? "safari"
						: "other";
	const device = /ipad|tablet|playbook|silk/i.test(ua)
		? "tablet"
		: /mobi|iphone|ipod|android/i.test(ua)
			? "phone"
			: "desktop";
	const viewport =
		width < 480
			? "xs"
			: width < 768
				? "sm"
				: width < 1024
					? "md"
					: width < 1440
						? "lg"
						: "xl";
	return Buyer.ClientProfileSchema.parse({
		os,
		browser,
		device,
		viewport,
		orientation: width >= height ? "landscape" : "portrait",
	});
}

type Unstamped<E> = Omit<E, "at" | "expiresAt">;
type Variant<N extends Buyer.TimelineEvent["name"]> = Extract<
	Buyer.TimelineEvent,
	{ name: N }
>;

/**
 * One timeline event with the two server stamps still missing — everything
 * the reconciler holds the moment before it writes, once it has joined in
 * whatever the page itself could not name.
 */
export type UnstampedEvent =
	| Unstamped<Variant<"page_view">>
	| Unstamped<Variant<"basket_change">>
	| Unstamped<Variant<"order_action">>;

/**
 * One timeline event as the page that caused it knows it: the shape above,
 * minus the checkout a retrieval came from. The ticket page is reached by a
 * capability token and is told the order's id by nothing — so the join is
 * the reconciler's to make or to decline, and no page invents one.
 */
export type TrackedEvent =
	| Extract<UnstampedEvent, { name: "page_view" | "basket_change" }>
	| Omit<Extract<UnstampedEvent, { name: "order_action" }>, "checkoutId">;

/** Every event a view or a reconciler has to report. One subscriber writes
 *  them; a track with nobody listening is the mountable case, not a leak. */
export const track$ = new Subject<TrackedEvent>();

/** The beat, and the budget a tab may spend beating. Thirty seconds is what
 *  the live panel's own 75-second window is drawn against; four hours is the
 *  point past which a tab left open is furniture rather than a visit. */
export const HEARTBEAT_INTERVAL_MS = 30_000;
export const HEARTBEAT_LIMIT_MS = 4 * 60 * 60 * 1000;

/**
 * The still-here beat: one immediately, then one every thirty seconds, for
 * as long as the tab is visible and no longer than four hours from the first
 * of them. A hidden tab is not a visit — its beats stop and resume with the
 * page itself, and the budget is anchored on the first real beat so a tab
 * opened in the background spends none of it before anyone has looked at the
 * page.
 *
 * `defer` because both readings are only true of the moment somebody
 * subscribes: this module is imported long before that, and the visibility
 * of the page at import time answers a question nobody asked.
 */
export const heartbeat$: Observable<void> = defer(() => {
	const visible$ = fromEvent(document, "visibilitychange").pipe(
		startWith(null),
		map(() => document.visibilityState === "visible"),
		distinctUntilChanged(),
	);
	return visible$.pipe(
		switchMap((visible) => (visible ? timer(0, HEARTBEAT_INTERVAL_MS) : EMPTY)),
		timestamp(),
		scan(
			(
				span: { readonly firstAt: number; readonly at: number } | null,
				beat,
			) => ({ firstAt: span?.firstAt ?? beat.timestamp, at: beat.timestamp }),
			null,
		),
		takeWhile(
			(span) => span !== null && span.at - span.firstAt < HEARTBEAT_LIMIT_MS,
		),
		map(() => undefined),
	);
});

/**
 * The visitor's standing objection to measurement, as their own buyer
 * document last stated it. Memory-backed on purpose: analytics writes
 * nothing to this device, so the objection lives where it is enforceable —
 * on the document every write is checked against — and this cell is only
 * what the switch renders. Null until this organization's record has been
 * read: the switch states nothing it has not been told.
 */
export const analyticsOptOut = state<boolean | null>(
	"analytics/opt-out",
).memory(null);

/** The switch's gesture (`analytics-opt-out.view.ts`): the value the visitor
 *  just asked for. The reconciler is the one subscriber, and it both writes
 *  the document and answers the cell above, so the switch performs no I/O
 *  and the cell keeps a single writer. */
export const analyticsOptOutRequest$ = new Subject<{
	readonly requestId: string;
	readonly value: boolean;
}>();
export const analyticsOptOutResult$ = new Subject<{
	readonly requestId: string;
	readonly value: boolean;
	readonly ok: boolean;
}>();

/**
 * The origin that sent this visitor here — the host alone, never the path,
 * which is the referring page's own content rather than a fact about this
 * arrival. Null for a direct arrival, for a move inside the shop itself, and
 * for anything unparseable: all three are the same non-answer, that nobody
 * sent this visit. Trimmed to the contract's own bound rather than dropped
 * past it — the write rule denies an over-long field, and a denied create
 * takes the whole record with it.
 */
export function referrerOrigin(
	referrer: string,
	ownOrigin: string,
): string | null {
	if (!URL.canParse(referrer)) return null;
	const origin = new URL(referrer).origin;
	if (origin === ownOrigin) return null;
	return origin.slice(0, Buyer.REFERRER_MAX_LENGTH);
}

/**
 * The campaign parameters this arrival carried, or null when it carried
 * none. Filtered by name against the contract's own five (`Buyer.UTM_KEYS`,
 * read off `UtmSchema`) and never by an `utm_` prefix — the write rule
 * closes the map to exactly those names and denies a create carrying any
 * sixth, so a link decorated with one more parameter must cost that
 * parameter rather than the whole record. An empty parameter is no
 * parameter.
 */
export function firstTouchUtm(search: string): Buyer.Utm | null {
	const params = new URLSearchParams(search);
	const captured = Object.fromEntries(
		Buyer.UTM_KEYS.map(
			(key) =>
				[
					key,
					(params.get(key) ?? "").slice(0, Buyer.UTM_VALUE_MAX_LENGTH),
				] as const,
		).filter(([, value]) => value.length > 0),
	);
	return Object.keys(captured).length === 0
		? null
		: Buyer.UtmSchema.parse(captured);
}
